/*!

=========================================================
* Black Dashboard React v1.2.2
=========================================================

* Product Page: https://www.creative-tim.com/product/black-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/black-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
/*eslint-disable*/
import React from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
// nodejs library to set properties for components
import { PropTypes } from "prop-types";

// javascript plugin used to create scrollbars on windows
import PerfectScrollbar from "perfect-scrollbar";

// reactstrap components
import { Nav, NavLink as ReactstrapNavLink } from "reactstrap";
import {
  BackgroundColorContext,
  backgroundColors,
} from "contexts/BackgroundColorContext";
import { usePermissions } from "../../hooks/usePermissions";
import { getIcon } from "../../utils/iconMapper";

var ps;

function Sidebar(props) {
  const location = useLocation();
  const sidebarRef = React.useRef(null);
  const [openCollapses, setOpenCollapses] = React.useState({});
  const manuallyClosedCollapses = React.useRef(new Set()); // Rastrear collapses cerrados manualmente
  const { canView } = usePermissions();
  
  // Mapeo de nombres de rutas a módulos del sistema
  // Nota: Los módulos en la BD pueden ser diferentes (ej: "Configuración" para Empresas/Sucursales)
  const routeToModuleMap = {
    'Empresas': 'Empresas', // También puede ser "Configuración" en la BD
    'Sucursales': 'Sucursales', // También puede ser "Configuración" en la BD
    'Clientes': 'Clientes',
    'Productos': 'Productos',
    'Categorías': 'Categorias',
    'Inventario': 'Inventario',
    'Ventas': 'Ventas',
    'Cotizaciones': 'Cotizaciones',
    'Facturación': 'Facturacion',
    'Usuarios': 'Usuarios',
    'Roles': 'Roles',
    'Permisos': 'Permisos',
    'Reportes': 'Reportes',
    'Auditoría': 'Auditoria',
  };
  
  // Filtrar rutas basándose en permisos
  const filterRoutesByPermissions = (routes) => {
    return routes.map(route => {
      // Si es un redirect, siempre mostrarlo
      if (route.redirect) return route;
      
      // Siempre mostrar Dashboard
      if (route.path === '/dashboard' || route.name === 'Dashboard') {
        return route;
      }
      
      // Si es un collapse, verificar permisos de las vistas hijas
      if (route.collapse && route.views) {
        const filteredViews = route.views.filter(view => {
          const moduleName = routeToModuleMap[view.name] || routeToModuleMap[route.name];
          // Si no está en el mapa, mostrarlo (puede ser una ruta especial)
          if (!moduleName) return true;
          // Verificar permiso
          return canView(moduleName);
        });
        
        // Si no hay vistas visibles, retornar null para filtrarlo después
        if (filteredViews.length === 0) return null;
        
        // Retornar una copia del route con las vistas filtradas, preservando todas las propiedades originales
        return { 
          ...route, 
          views: filteredViews,
          state: route.state, // Asegurar que state se preserve
        };
      }
      
      // Para rutas normales, verificar permiso
      const moduleName = routeToModuleMap[route.name];
      // Si no está en el mapa, mostrarlo (Dashboard, etc.)
      if (!moduleName) return route;
      
      // Verificar permiso, si no tiene permiso, ocultar
      return canView(moduleName) ? route : null;
    }).filter(route => route !== null);
  };
  
  // verifies if routeName is the one active (in browser input)
  const activeRoute = (routeName) => {
    return location.pathname === routeName ? "active" : "";
  };
  
  // verifies if any child route is active
  const isChildActive = React.useCallback((views) => {
    if (!views) return false;
    return views.some(view => {
      const fullPath = view.layout + view.path;
      return location.pathname === fullPath;
    });
  }, [location.pathname]);
  
  // toggle collapse
  const toggleCollapse = (state) => {
    setOpenCollapses(prev => {
      const newState = !prev[state];
      // Si el usuario cierra manualmente, agregarlo al set de cerrados manualmente
      if (!newState) {
        manuallyClosedCollapses.current.add(state);
      } else {
        // Si el usuario lo abre, removerlo del set
        manuallyClosedCollapses.current.delete(state);
      }
      return {
        ...prev,
        [state]: newState
      };
    });
  };
  
  React.useEffect(() => {
    if (navigator.platform.indexOf("Win") > -1) {
      ps = new PerfectScrollbar(sidebarRef.current, {
        suppressScrollX: true,
        suppressScrollY: false,
      });
    }
    // Specify how to clean up after this effect:
    return function cleanup() {
      if (navigator.platform.indexOf("Win") > -1) {
        ps.destroy();
      }
    };
  });
  
  const linkOnClick = () => {
    document.documentElement.classList.remove("nav-open");
  };
  const { routes: originalRoutes, rtlActive, logo } = props;
  
  // Filtrar rutas según permisos
  const routes = React.useMemo(() => {
    return filterRoutesByPermissions(originalRoutes);
  }, [originalRoutes, canView]);
  
  // Auto-open collapse if child is active (solo cuando cambia la ruta activa)
  // Usar useRef para rastrear la última ruta activa y evitar loops
  const lastActivePath = React.useRef(location.pathname);
  
  React.useEffect(() => {
    // Solo ejecutar si la ruta realmente cambió
    if (lastActivePath.current === location.pathname) {
      return;
    }
    lastActivePath.current = location.pathname;
    
    originalRoutes.forEach(route => {
      if (route.collapse && route.views && route.state) {
        // Verificar si alguna vista hija está activa
        const hasActiveChild = route.views.some(view => {
          const fullPath = view.layout + view.path;
          return location.pathname === fullPath;
        });
        
        // Solo abrir automáticamente si hay una ruta activa Y no fue cerrado manualmente
        if (hasActiveChild && !manuallyClosedCollapses.current.has(route.state)) {
          setOpenCollapses(prev => {
            // Solo actualizar si no está ya abierto para evitar re-renders innecesarios
            if (prev[route.state]) return prev;
            return {
              ...prev,
              [route.state]: true
            };
          });
        }
      }
    });
  }, [location.pathname, originalRoutes]); // Usar originalRoutes en lugar de routes filtradas
  let logoImg = null;
  if (logo !== undefined) {
    if (logo.outterLink !== undefined) {
      logoImg = (
        <a
          href={logo.outterLink}
          className="simple-text logo-normal"
          target="_blank"
          onClick={props.toggleSidebar}
        >
          <div className="logo-img" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0.5rem 0' }}>
            <img src={logo.imgSrc} alt="MaeCore logo" style={{ maxWidth: '180px', height: 'auto', display: 'block' }} />
          </div>
        </a>
      );
    } else {
      logoImg = (
        <Link
          to={logo.innerLink}
          className="simple-text logo-normal"
          onClick={props.toggleSidebar}
        >
          <div className="logo-img" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0.5rem 0' }}>
            <img src={logo.imgSrc} alt="MaeCore logo" style={{ maxWidth: '180px', height: 'auto', display: 'block' }} />
          </div>
        </Link>
      );
    }
  }
  return (
    <BackgroundColorContext.Consumer>
      {({ color }) => (
        <div className="sidebar" data={color}>
          <div className="sidebar-wrapper" ref={sidebarRef}>
            {logoImg !== null ? (
              <div className="logo">
                {logoImg}
              </div>
            ) : null}
            <Nav>
              {routes.map((prop, key) => {
                if (prop.redirect) return null;
                
                // Handle collapsible menu items
                if (prop.collapse) {
                  const isOpen = openCollapses[prop.state] || false;
                  const isActive = isChildActive(prop.views);
                  
                  return (
                    <li
                      className={
                        (isActive ? "active" : "") + (prop.pro ? " active-pro" : "")
                      }
                      key={key}
                    >
                      <a
                        href="#"
                        className="nav-link"
                        data-toggle="collapse"
                        aria-expanded={isOpen}
                        onClick={(e) => {
                          e.preventDefault();
                          toggleCollapse(prop.state);
                        }}
                      >
                        <i className={getIcon(prop.icon)} />
                        <p>{rtlActive ? prop.rtlName : prop.name}</p>
                        <b className="caret" />
                      </a>
                      <div className={`collapse ${isOpen ? "show" : ""}`}>
                        <ul className="nav">
                          {prop.views.map((view, viewKey) => {
                            const fullPath = view.layout + view.path;
                            const isViewActive = location.pathname === fullPath;
                            
                            return (
                              <li
                                className={isViewActive ? "active" : ""}
                                key={viewKey}
                              >
                                <NavLink
                                  to={fullPath}
                                  className="nav-link"
                                  onClick={props.toggleSidebar}
                                >
                                  <span className="sidebar-mini-icon">
                                    <i className={getIcon(view.icon || "tim-icons icon-bullet-list-67")} />
                                  </span>
                                  <span className="sidebar-normal">
                                    {rtlActive ? view.rtlName : view.name}
                                  </span>
                                </NavLink>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </li>
                  );
                }
                
                // Handle regular menu items
                if (!prop.path) return null;
                
                return (
                  <li
                    className={
                      activeRoute(prop.layout + prop.path) + (prop.pro ? " active-pro" : "")
                    }
                    key={key}
                  >
                    <NavLink
                      to={prop.layout + prop.path}
                      className="nav-link"
                      onClick={props.toggleSidebar}
                    >
                      <i className={getIcon(prop.icon)} />
                      <p>{rtlActive ? prop.rtlName : prop.name}</p>
                    </NavLink>
                  </li>
                );
              })}
            </Nav>
          </div>
        </div>
      )}
    </BackgroundColorContext.Consumer>
  );
}

Sidebar.propTypes = {
  // if true, then instead of the routes[i].name, routes[i].rtlName will be rendered
  // insde the links of this component
  rtlActive: PropTypes.bool,
  routes: PropTypes.arrayOf(PropTypes.object),
  logo: PropTypes.shape({
    // innerLink is for links that will direct the user within the app
    // it will be rendered as <Link to="...">...</Link> tag
    innerLink: PropTypes.string,
    // outterLink is for links that will direct the user outside the app
    // it will be rendered as simple <a href="...">...</a> tag
    outterLink: PropTypes.string,
    // the text of the logo
    text: PropTypes.node,
    // the image src of the logo
    imgSrc: PropTypes.string,
  }),
};

export default Sidebar;
